// Helpers for keeping an event's `audioFile` and `peaksData` in sync.
//
// Whenever we produce or receive new audio for an Audio Description event
// (TTS render, voice recording, link refresh) we also want fresh peak data
// available for the timeline. This module centralizes that pairing so all
// call sites stay consistent and we never end up with `audioFile` set but
// stale or missing `peaksData`.

import { extractPeaksFromUrl } from "@app/lib/timeline/peaks.js";
import { environment } from "@app/store/envStore.svelte.js";
import { stampAudioRenderHash } from "@app/lib/audio/audioRenderState.js";
import { parseStoragePathFromUrl } from "@app/lib/audio/refreshEventAudioUrl.js";

const SAMPLES_PER_SECOND = 100;

/**
 * Assign a new audio file to an event and extract its peaks. Always clears
 * any prior peak data first so the timeline never shows a stale waveform
 * before the new one is ready.
 *
 * Failure of peak extraction is non-fatal -- the audio still plays, the
 * region just renders without a waveform until extraction succeeds.
 *
 * @param {object} event - reactive Event object from eventGroupState
 * @param {object} payload
 * @param {string} [payload.audioFile]
 * @param {string|false} [payload.audioFileUuid]
 * @param {number} [payload.audioFileDuration]
 * @param {string} [payload.audioFileRemote]
 * @returns {Promise<void>}
 */
export async function assignEventAudio(event, { audioFile, audioFileUuid, audioFileDuration, audioFileRemote } = {}) {
    if (!event) return;
    if (audioFile !== undefined) event.audioFile = audioFile;
    if (audioFileUuid !== undefined) event.audioFileUuid = audioFileUuid;
    if (typeof audioFileDuration === "number") event.audioFileDuration = audioFileDuration;
    if (audioFileRemote !== undefined) {
        event.audioFileRemote = audioFileRemote;
    } else if (event.audioFile) {
        const parsed = parseStoragePathFromUrl(event.audioFile);
        if (parsed) event.audioFileRemote = parsed;
    }
    event.rendered = true;
    stampAudioRenderHash(event);
    event.peaksData = [];
    event.peaksSampleRate = 0;
    if (!event.audioFile) return;
    // Parent-hosted (iframe) audio is cross-origin without CORS headers; fetching
    // it for waveform peaks would throw CORS at the network layer. Skip extraction
    // and leave a flat baseline. Audio playback does not require CORS.
    if (environment.adLicense) return;
    try {
        const peaks = await extractPeaksFromUrl(event.audioFile, { samplesPerSecond: SAMPLES_PER_SECOND });
        const channel = peaks?.data?.[0];
        if (Array.isArray(channel) && channel.length > 0) {
            event.peaksData = channel;
            event.peaksSampleRate = SAMPLES_PER_SECOND;
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[assignEventAudio] peak extraction failed:", err?.message);
    }
}

/**
 * Clear all audio + peak state for an event. Use this in the places that
 * previously set `event.audioFile = ""` / `event.rendered = false` to keep
 * peaks from going stale.
 *
 * @param {object} event
 */
export function clearEventAudio(event) {
    if (!event) return;
    event.audioFile = "";
    event.audioFileUuid = false;
    event.audioFileRemote = "";
    event.audioFileDuration = false;
    event.peaksData = [];
    event.peaksSampleRate = 0;
    event.rendered = false;
    event.audioRenderHash = "";
}

/**
 * Extract peaks for an event that already has an `audioFile` but no peaks
 * (e.g. legacy projects rendered before per-event waveforms existed). Does
 * not modify `audioFile`, `audioFileDuration`, or `rendered` so it is safe
 * to call as a lazy backfill from the timeline.
 *
 * @param {object} event
 * @returns {Promise<void>}
 */
export async function ensureEventPeaks(event) {
    if (!event || !event.audioFile) return;
    if (Array.isArray(event.peaksData) && event.peaksData.length > 0) return;
    // Skip cross-origin peak fetch for parent-hosted (iframe) audio to avoid CORS.
    if (environment.adLicense) return;
    try {
        const peaks = await extractPeaksFromUrl(event.audioFile, { samplesPerSecond: SAMPLES_PER_SECOND });
        const channel = peaks?.data?.[0];
        if (Array.isArray(channel) && channel.length > 0) {
            event.peaksData = channel;
            event.peaksSampleRate = SAMPLES_PER_SECOND;
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[ensureEventPeaks] peak extraction failed:", err?.message);
    }
}
